import { Link } from "react-router-dom";
import { ArrowRight, Boxes, ClipboardList, GitCompareArrows, Handshake, Search, Send, Wallet } from "lucide-react";
import { StatusLegend } from "@/components/StatusChip";
import { Btn, Card, SectionTitle } from "@/components/Ui";

const BUYER_STEPS = [
  { Icon: Search, t: "Search by part number or specification", d: "Find the exact item, or search on the technical parameters you actually need." },
  { Icon: GitCompareArrows, t: "Compare technically equivalent products", d: "Every mandatory and optional parameter is assessed individually — no percentage scores." },
  { Icon: Send, t: "Submit an RFQ", d: "The specification prefills. Add quantity, dates, delivery location, Incoterm, certificates and attachments." },
  { Icon: Boxes, t: "Matched suppliers quote", d: "Only suppliers whose category, brand and market coverage match receive the enquiry." },
  { Icon: Wallet, t: "Compare landed cost", d: "Product price, freight, duty, clearance and local delivery are shown side by side as estimates." },
  { Icon: Handshake, t: "Award direct or managed by ADIT", d: "Buy directly from the supplier, or ask ADIT Trading to handle the procurement." },
];

const SUPPLIER_STEPS = [
  "Register your company, brands, categories and markets served",
  "ADIT verifies your trade licence, certificates and product data",
  "Receive only the enquiries that match your capability",
  "Submit a structured technical and commercial quotation",
  "Confirm seller responsibility for the offer you make",
  "Track won and lost opportunities in your dashboard",
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <section className="border-b border-[#DFE6ED] bg-[#102A43]">
        <div className="mx-auto max-w-[1240px] px-5 py-16 lg:py-20">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#D9A441]">
            How ADIT Connect works
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            Specification first. Commercial second. Decision documented.
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#bcccdc] md:text-base">
            ADIT Connect exists because most industrial sourcing errors are specification errors, not
            price errors. The platform normalizes technical data first, then puts commercial offers
            beside it so the buyer can decide with evidence.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-5 py-14 lg:py-20">
        <SectionTitle overline="For buyers" title="The buyer workflow" />
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {BUYER_STEPS.map((s, i) => (
            <Card key={s.t} data-testid={`buyer-step-${i + 1}`} className="reveal" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex items-center justify-between">
                <span className="grid h-10 w-10 place-items-center rounded-[9px] bg-[#eef2f7] text-[#102A43]">
                  <s.Icon className="h-5 w-5" />
                </span>
                <span className="font-display text-xl font-black text-[#DFE6ED]">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="mt-4 font-display text-base font-bold tracking-tight text-[#102A43]">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#667788]">{s.d}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="border-y border-[#DFE6ED] bg-white">
        <div className="mx-auto max-w-[1240px] px-5 py-14 lg:py-20">
          <SectionTitle
            overline="Conformity"
            title="Three outcomes per parameter"
            sub="Mandatory parameters are separated from optional ones. A deviation on an optional parameter is a discussion; a failure on a mandatory parameter disqualifies the offer."
          />
          <div className="mt-6">
            <StatusLegend />
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              ["Meets Requirement", "The offered value satisfies the required value, or exceeds it where higher is better (IP rating, gas group, EPL, cable range)."],
              ["Technical Deviation", "The offered value differs but may still be acceptable — the buyer decides, with the difference stated explicitly."],
              ["Does Not Meet Requirement", "The offered value fails a mandatory parameter, or the parameter is not declared at all."],
            ].map(([t, d]) => (
              <Card key={t}>
                <h3 className="font-display text-base font-bold tracking-tight text-[#102A43]">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#667788]">{d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-5 py-14 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <SectionTitle overline="For suppliers" title="The supplier workflow" />
            <ol className="mt-7 space-y-3">
              {SUPPLIER_STEPS.map((s, i) => (
                <li key={s} className="flex gap-3 text-sm text-[#334e68]">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#102A43] text-[10px] font-bold text-white">
                    {i + 1}
                  </span>
                  {s}
                </li>
              ))}
            </ol>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/register?role=supplier">
                <Btn data-testid="hiw-supplier-register-btn">Register as a supplier</Btn>
              </Link>
              <Link to="/pricing">
                <Btn variant="ghost" data-testid="hiw-pricing-btn">See subscription plans</Btn>
              </Link>
            </div>
          </div>
          <Card>
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[#147D78]" />
              <h3 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
                Award routes
              </h3>
            </div>
            <div className="mt-5 space-y-5">
              <div>
                <p className="font-display text-sm font-bold text-[#102A43]">Direct purchase</p>
                <p className="mt-1.5 text-sm leading-relaxed text-[#667788]">
                  The buyer purchases directly from the selected supplier. That supplier is
                  responsible for the product, technical conformity, delivery, documentation,
                  warranty, returns and all other seller obligations. ADIT Connect acts as the
                  sourcing and specification platform.
                </p>
              </div>
              <div className="border-t border-[#DFE6ED] pt-5">
                <p className="font-display text-sm font-bold text-[#102A43]">Managed by ADIT</p>
                <p className="mt-1.5 text-sm leading-relaxed text-[#667788]">
                  The buyer asks ADIT Trading to handle the procurement. This raises a Managed
                  Procurement Request for review. ADIT does not become the seller until it reviews
                  and accepts that specific request.
                </p>
              </div>
            </div>
            <Link to="/rfq" className="mt-7 inline-block">
              <Btn variant="gold" data-testid="hiw-rfq-btn">
                Submit an RFQ <ArrowRight className="h-4 w-4" />
              </Btn>
            </Link>
          </Card>
        </div>
      </section>
    </div>
  );
}
